import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import TiltCard from "./TiltCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Portfolio() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/projects`, {
          params: { featured: true, limit: 6 },
        });
        if (!live) return;
        if (Array.isArray(data)) {
          setProjects(data.slice(0, 6));
        }
      } catch {
        if (live) setProjects([]);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  return (
    <section
      id="work"
      data-testid="portfolio-section"
      className="relative bg-[#050505] py-32 md:py-48 border-t border-white/[0.06]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-20 md:mb-24">
          <div className="md:col-span-4">
            <div className="font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white/50">
              (03) — Selected Work
            </div>
          </div>
          <div className="md:col-span-8 flex items-end justify-between gap-6">
            <h2 className="font-display font-extrabold uppercase leading-[0.9] tracking-tighter text-4xl md:text-6xl lg:text-7xl">
              Shipped, scaled,<br />
              <span className="text-[#29ABE2]/80">screenshotted.</span>
            </h2>
            <Link
              to="/portfolio"
              data-testid="portfolio-cta-archive"
              className="hidden md:inline-flex items-center gap-2 font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white/60 hover:text-[#29ABE2]"
            >
              Full archive
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="text-white/40 text-sm">Loading projects…</div>
        ) : projects.length === 0 ? (
          <div className="text-white/40 text-sm py-20 text-center border border-white/[0.06]">
            No featured projects yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {projects.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="group"
                data-testid={`project-card-${p.id}`}
              >
                <TiltCard project={p} index={i} />
                <div className="mt-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono-accent text-[11px] uppercase tracking-[0.3em] text-[#29ABE2]">
                      {p.client}
                    </div>
                    <div className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-white/40">
                      {p.year}
                    </div>
                  </div>
                  <h3 className="font-display font-bold text-xl md:text-2xl tracking-tight leading-[1.05]">
                    {p.title}
                  </h3>
                  <div className="text-white/50 text-[11px] uppercase tracking-[0.2em] font-mono-accent mt-1.5">
                    {p.discipline}
                  </div>
                  <p className="text-white/60 text-sm leading-relaxed mt-4 line-clamp-3">
                    {p.description}
                  </p>
                  {p.case_url && (
                    <a
                      href={p.case_url}
                      target={p.case_url.startsWith("http") ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-5 font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white border-b border-white/30 pb-0.5 hover:text-[#29ABE2] hover:border-[#29ABE2]"
                      data-testid={`project-case-${p.id}`}
                    >
                      Case study <ArrowUpRight size={12} />
                    </a>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        )}

        <div className="mt-20 md:mt-24 flex justify-center">
          <Link
            to="/portfolio"
            data-testid="portfolio-view-all"
            className="group inline-flex items-center gap-3 px-7 py-3.5 rounded-full border border-white/[0.15] text-white text-sm hover:bg-[#29ABE2] hover:text-black hover:border-[#29ABE2] transition-colors"
          >
            View full portfolio
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}
